import { AdBanner } from "./AdBanner.tsx";
import { ThemeToggle } from "./ThemeToggle.tsx";
import { cn } from "./cn.ts";

export interface AppShellProps {
  appName: string;
  tagline?: string;
  icon?: React.ReactNode;
  /** Right-hand header actions, before the theme toggle. */
  actions?: React.ReactNode;
  children: React.ReactNode;
  /** Ad slot ids; the banners stay inert placeholders until consent exists. */
  topSlot?: string;
  className?: string;
  /** Wider max-width for canvas-style apps. */
  width?: "default" | "wide" | "full";
  /**
   * Pin the shell to exactly one viewport and hand the full remaining area to
   * the child, with no padding. For apps whose main surface is a canvas that
   * must not scroll. Default layout is unchanged.
   */
  fill?: boolean;
}

/** The suite's hub; every app links back to it so the sites cross-navigate. */
export const HOME_URL = "https://ottercraft.dev";

const widths = {
  default: "max-w-3xl",
  wide: "max-w-6xl",
  full: "max-w-none",
} as const;

export function AppShell({
  appName,
  tagline,
  icon,
  actions,
  children,
  topSlot,
  className,
  width = "default",
  fill = false,
}: AppShellProps) {
  return (
    <div
      className={cn(
        "flex flex-col",
        fill ? "h-dvh overflow-hidden" : "min-h-dvh",
      )}
    >
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-surface focus:px-3 focus:py-2 focus:text-sm focus:shadow-lg"
      >
        Skip to content
      </a>

      <header className="sticky top-0 z-30 border-b border-border bg-bg/85 backdrop-blur-md">
        <div
          className={cn(
            "mx-auto flex h-14 items-center gap-3 px-4",
            widths[width],
          )}
        >
          <div className="flex min-w-0 items-center gap-2.5">
            {icon ? <span className="text-accent">{icon}</span> : null}
            <div className="min-w-0">
              <h1 className="truncate text-sm font-semibold tracking-tight">
                {appName}
              </h1>
              {tagline ? (
                <p className="hidden truncate text-xs text-fg-muted sm:block">
                  {tagline}
                </p>
              ) : null}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            {actions}
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main
        id="main"
        className={cn(
          "mx-auto w-full flex-1",
          fill ? "min-h-0" : "px-4 py-6",
          widths[width],
          className,
        )}
      >
        {topSlot ? <AdBanner slot={topSlot} format="leaderboard" className="mb-6" /> : null}
        {children}
      </main>

      {fill ? null : (
      <footer className="border-t border-border">
        <div
          className={cn(
            "mx-auto flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-5 text-xs text-fg-muted",
            widths[width],
          )}
        >
          <span>Runs entirely in your browser. Nothing is uploaded.</span>
          <nav className="ml-auto flex flex-wrap gap-4" aria-label="Site">
            <a className="hover:text-fg" href={HOME_URL}>
              More tools
            </a>
            <a className="hover:text-fg" href={`${HOME_URL}/about`}>
              About
            </a>
            <a className="hover:text-fg" href={`${HOME_URL}/contact`}>
              Contact
            </a>
            <a className="hover:text-fg" href="/privacy">
              Privacy
            </a>
            <a className="hover:text-fg" href="/terms">
              Terms
            </a>
          </nav>
        </div>
      </footer>
      )}
    </div>
  );
}
